import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCollectionStore } from '../lib/collectionStore';
import { useProfileStore } from '../lib/supabase';
import { getAllSets } from '../services/pokemonTcgApi';
import { PokemonSet } from '../types';
import { ArrowLeft } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingHeader from '../components/LoadingHeader';
import CollectionStats from '../components/CollectionStats';
import TotalCardsView from './collection/TotalCardsView';
import UniqueCardsView from './collection/UniqueCardsView';
import SeriesView from './collection/SeriesView';
import PortfolioView from './collection/PortfolioView';
import { Helmet } from 'react-helmet-async';

export default function CollectionPage() {
  const { collection, loadCollection } = useCollectionStore();
  const { isAuthenticated } = useProfileStore();
  const [sets, setSets] = useState<PokemonSet[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState<'total' | 'unique' | 'series' | 'portfolio'>('total');
  const [totalStats, setTotalStats] = useState({
    totalCards: 0,
    uniqueCards: 0,
    setsCollected: 0,
    totalSets: 0,
    totalCardsInDatabase: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        if (isAuthenticated) {
          await loadCollection();
        }
        const allSets = await getAllSets();
        
        // Calculate database totals
        const totalCardsInDatabase = allSets.reduce((sum, set) => sum + set.total, 0);
        
        setSets(allSets);
        setTotalStats(prev => ({
          ...prev,
          totalCardsInDatabase,
          totalSets: allSets.length
        }));
      } catch (error) {
        console.error('Error loading collection page:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loadCollection, isAuthenticated]);

  // Calculate total stats whenever collection changes
  useEffect(() => {
    const uniqueSetIds = new Set(collection.map(card => card.setId));
    
    const stats = {
      totalCards: collection.reduce((sum, card) => sum + card.quantity, 0),
      uniqueCards: collection.length,
      setsCollected: uniqueSetIds.size,
      totalSets: sets.length,
      totalCardsInDatabase: totalStats.totalCardsInDatabase
    };
    
    setTotalStats(stats);
  }, [collection, sets.length, totalStats.totalCardsInDatabase]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <div className="bg-white/5 border-b border-white/10">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="mb-4">
              <Breadcrumbs />
            </div>
            <LoadingHeader />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>My Collection | PokeCardIndex</title>
        <meta 
          name="description" 
          content={`Track and manage your Pokémon card collection. View statistics, completion rates, and card values across ${totalStats.totalSets} sets.`}
        />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://pokecardindex.com/collection" />
      </Helmet>

      <div className="bg-white/5 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="mb-4">
            <Breadcrumbs />
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-white/60 hover:text-white transition-colors flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back
            </Link>
            <h1 className="text-2xl font-bold">My Collection</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="relative isolate">
          <CollectionStats 
            stats={totalStats} 
            activeTab={activeView}
            onTabChange={setActiveView}
          />

          <div className="mt-8">
            {activeView === 'total' && <TotalCardsView />}
            {activeView === 'unique' && <UniqueCardsView />}
            {activeView === 'series' && <SeriesView />}
            {activeView === 'portfolio' && <PortfolioView />}
          </div>
        </div>
      </div>
    </div>
  );
}